import { isDefined, unreachable } from '../../../ts-utils';
import memoize from 'lodash/memoize';
const getCommentOwnerById = (state, resourceId) => (commentId) => {
    const resourceState = state.comments[resourceId];
    if (resourceState?.type !== 'READY') {
        return ownerCache.getUnknown();
    }
    const comment = resourceState.commentsById[commentId];
    if (!comment) {
        return ownerCache.getUnknown();
    }
    return getOwner(comment, state.members);
};
export const getCurrentOwner = (state) => state.members.currentOwner;
export const getCurrentMember = (state) => {
    const currentOwner = state.members.currentOwner;
    return currentOwner.type === 'MEMBER' ? currentOwner.member : undefined;
};
export const getMemberCache = (state) => state.members.membersCache;
export const getIsCurrentUserAuthenticated = (state) => getCurrentMember(state)?.profile !== undefined;
export const getMemberById = (state, memberId) => (memberId ? state.members.membersCache[memberId] : undefined);
export const getIsMemberCheckPending = (state) => state.members.memberCheckPending;
export const getMemberBadge = (state, memberId, index = 0) => {
    const member = getMemberById(state, memberId);
    return member?.memberBadges?.[index] ?? undefined;
};
export const getMemberRole = (state, memberId, index = 0) => {
    const member = getMemberById(state, memberId);
    return member?.memberRoles?.[index] ?? undefined;
};
export const getMemberRoles = (state, memberId) => {
    const member = getMemberById(state, memberId);
    return member?.memberRoles ?? [];
};
export const getMembers = (state, memberIds) => {
    return Object.values(state.members.membersCache).filter((member) => memberIds.includes(member.id));
};
// To have better render optimization we do cache 'owner' objects whenever possible
const ownerCache = (() => {
    const ownerMemberCache = new WeakMap();
    const getAnonymous = memoize((id) => ({
        type: 'ANONYMOUS',
        identityId: id,
    }));
    const UNKNOWN_OWNER = { type: 'UNKNOWN' };
    const PLACEHOLDER_OWNER = { type: 'PLACEHOLDER' };
    return {
        getMember: (member) => {
            if (ownerMemberCache.has(member)) {
                return ownerMemberCache.get(member);
            }
            else {
                const owner = { type: 'MEMBER', member };
                ownerMemberCache.set(member, owner);
                return owner;
            }
        },
        getAnonymous,
        getUnknown: () => UNKNOWN_OWNER,
        getPlaceholder: () => PLACEHOLDER_OWNER,
    };
})();
export const getOwnerForReplyingTo = (state, resourceId, replyState) => {
    const membersState = state.members;
    switch (replyState.type) {
        case 'REPLY_TYPING':
        case 'REPLY_CREATE_PENDING':
        case 'REPLY_CREATE_ERROR':
        case 'REPLY_CREATE_ERROR_EDITING':
        case 'REPLY_DELETED':
        case 'REPLY_LIVE_PENDING':
            const { targetId } = replyState;
            const owner = getCommentOwnerById(state, resourceId)(targetId);
            return owner;
        default:
            const author = replyState.comment.replyTo?.author;
            return resolveOwner({ author, membersState, fallback: ownerCache.getUnknown() });
    }
};
export const getOwner = (commentState, membersState) => {
    switch (commentState.type) {
        case 'COMMENT_CREATE_PENDING':
        case 'REPLY_CREATE_PENDING': {
            if (membersState.currentOwner.type === 'MEMBER') {
                return membersState.currentOwner;
            }
            if (commentState.asGuest) {
                return membersState.currentOwner.type === 'ANONYMOUS'
                    ? { ...membersState.currentOwner, authorName: commentState.authorName }
                    : membersState.currentOwner;
            }
            return ownerCache.getPlaceholder();
        }
        case 'COMMENT_CREATE_ERROR_EDITING':
        case 'COMMENT_CREATE_ERROR':
        case 'REPLY_CREATE_ERROR':
        case 'REPLY_CREATE_ERROR_EDITING': {
            if (membersState.currentOwner.type === 'MEMBER') {
                return membersState.currentOwner;
            }
            if (commentState.asGuest) {
                return membersState.currentOwner.type === 'ANONYMOUS'
                    ? { ...membersState.currentOwner, authorName: commentState.authorName }
                    : membersState.currentOwner;
            }
            return ownerCache.getUnknown();
        }
        case 'REPLY_TYPING': {
            return membersState.currentOwner;
        }
        case 'COMMENT_LIVE_PENDING':
        case 'REPLY_LIVE_PENDING': {
            const author = commentState.author;
            return resolveOwner({ author, membersState, fallback: ownerCache.getPlaceholder() });
        }
        case 'REPLY_DELETED':
        case 'COMMENT_DELETED': {
            return ownerCache.getUnknown();
        }
        default:
            const author = commentState.comment.author;
            return resolveOwner({ author, membersState, fallback: ownerCache.getUnknown() });
    }
};
export const getOwnerName = (owner, t) => {
    switch (owner.type) {
        case 'MEMBER': {
            return resolveMemberName(owner.member) ?? t('user-info.unknown-member');
        }
        case 'ANONYMOUS': {
            return owner?.authorName ?? t('user-info.guest-commenter', { id: undefined });
        }
        case 'PLACEHOLDER': {
            // To preserve vertical space - unbreakable space
            return '\u00A0';
        }
        case 'UNKNOWN': {
            return t('user-info.unknown-member');
        }
        default:
            throw unreachable(owner);
    }
};
export const resolveMemberName = (member) => {
    if (member.profile?.nickname) {
        return member.profile?.nickname;
    }
    if (member.contact?.firstName || member.contact?.lastName) {
        return [member.contact?.firstName, member.contact?.lastName].filter(isDefined).join(' ');
    }
};
export const getOwnerId = (owner) => {
    switch (owner.type) {
        case 'MEMBER':
            return owner.member.id;
        case 'ANONYMOUS':
            return owner.identityId;
        case 'UNKNOWN':
        case 'PLACEHOLDER':
            return undefined;
        default:
            throw unreachable(owner);
    }
};
const resolveOwner = ({ author, membersState, fallback, }) => {
    if (!author) {
        return fallback;
    }
    switch (author.identityType) {
        case 'MEMBER':
            const member = author.identityId && membersState.membersCache[author.identityId];
            return member ? ownerCache.getMember(member) : fallback;
        case 'ANONYMOUS':
            return author.identityId
                ? { ...ownerCache.getAnonymous(author.identityId), authorName: author.authorName }
                : fallback;
        default:
            return fallback;
    }
};
export const getReactionOwner = (membersCache, id) => {
    const member = membersCache[id];
    // we can't check by IdentityType, as reactions always have member IdentityType :(
    return member ? ownerCache.getMember(member) : ownerCache.getAnonymous(id);
};
export const getReactionOwnerId = (owner) => owner.type === 'MEMBER' ? owner.member.id : owner.identityId;
export const getFollowedMemberById = (state, id) => state.members.followedMembers.find((m) => m.id === id);
export const getCurrentOwnerId = (currentOwner) => {
    switch (currentOwner.type) {
        case 'MEMBER':
            return currentOwner.member.id;
        case 'ANONYMOUS':
            return currentOwner.identityId;
        case 'UNKNOWN':
            return undefined;
        default:
            throw unreachable(currentOwner);
    }
};
export const extractCurrentOwnerIdentity = (state) => {
    const identityId = getCurrentOwnerId(state.currentOwner);
    if (identityId) {
        return {
            identityId,
            identityType: state.currentOwner.type === 'MEMBER' ? 'MEMBER' : 'ANONYMOUS',
        };
    }
};
//# sourceMappingURL=members-selectors.js.map